import { Option } from "rc-select";
import { useEffect, useMemo, useRef, useState } from "react";

import { SelectData } from "../../../models";

interface useAutocompleteProps {
  value?: string | null;
  initialKey?: string | null;
  initialValue?: string | null;
  data: SelectData;
  onSelect: (value: string) => void;
  onChange?: (value: string) => void;
  onTypeSearch?: (value: string) => void;
  clearOnSelect?: boolean;
  // Key of an action option, e.g. CompanyPicker's "Add new company" row: it survives search
  // filtering, and selecting it reports the key without becoming the field's value.
  alwaysVisibleKey?: string | number;
}

interface useAutocompleteReturnData {
  currentValue: string | null | undefined;
  currentKey: string | null | undefined;
  options: JSX.Element[];
  handleSearch: (newVal: string) => void;
  handleSelect: (newKey: string | number) => void;
}

const TYPE_SEARCH_WAITING_TIME = 400;

export const useAutocomplete = ({
  value,
  initialKey,
  initialValue,
  data,
  onSelect,
  onChange,
  onTypeSearch,
  clearOnSelect = false,
  alwaysVisibleKey,
}: useAutocompleteProps): useAutocompleteReturnData => {
  const [currentValue, setCurrentValue] = useState(value);
  const [currentKey, setCurrentKey] = useState(initialKey);
  const [visibleData, setVisibleData] = useState<SelectData>(data);

  // Show every option again whenever a fresh data set arrives — the caller has usually just
  // server-filtered it, and its results need not literally contain the typed text.
  useEffect(() => {
    setVisibleData(data);
  }, [data]);

  // Options are valued by key, not by display label: two rows can share a label, and rc-select
  // resolves the selected option by its value, so labels would collapse duplicates onto one key.
  // In combobox mode the input text comes from the controlled `value` prop, never from the option,
  // so the key is free to be the value.
  const options = useMemo(
    () =>
      visibleData.map((kvp) => (
        <Option
          key={kvp.key}
          value={kvp.key}
          className={kvp.key?.toString() === currentKey ? "Autocomplete-item_selected" : undefined}
        >
          {kvp.value}
        </Option>
      )),
    [visibleData, currentKey]
  );

  const applyFilter = (newVal: string): void => {
    setVisibleData(
      data.filter(
        (kvp) =>
          (alwaysVisibleKey !== undefined && kvp.key === alwaysVisibleKey) ||
          kvp.value?.toString().toLowerCase().includes(newVal.toLowerCase())
      )
    );
  };

  /*
    Set when typing withdrew the key, so the sync effect below can tell that transition apart from a reset the
    caller made on its own. Callers answer the onSelect("") here with a null initialKey, which is not a reason
    to blank what is being typed.
  */
  const clearedByTypingRef = useRef(false);

  // rc-select fires this on typing only; selection is handled by handleSelect.
  const handleSearch = (newVal: string): void => {
    setCurrentValue(newVal);
    setCurrentKey("");
    clearedByTypingRef.current = true;
    onSelect("");
    if (onChange) {
      onChange(newVal);
    }
    applyFilter(newVal);
  };

  const handleSelect = (newKey: string | number): void => {
    const el = data.find((kvp) => kvp.key?.toString() === newKey?.toString());
    if (el) {
      const selectedKey = el.key?.toString();
      if (alwaysVisibleKey !== undefined && el.key === alwaysVisibleKey) {
        onSelect(selectedKey);
        /*
          rc-select has just taken the action row's key as its value — in combobox mode it syncs the input
          from whatever was selected. Push the field's own text back so the action cannot show as a
          selection. Until the user types, currentValue is undefined and the field is uncontrolled, which is
          what let the key through in the first place.
        */
        setCurrentValue(initialValue ?? "");
        /*
          The key goes back with the text. Left empty, the field reads as "typing" rather than "still holding
          its selection", and the debounce below would report the restored name as a fresh search term — which
          then filtered the very draft the action is about to create out of the caller's option list.
        */
        setCurrentKey(initialKey ?? "");
        return;
      }

      const selectedValue = el.value?.toString() ?? "";
      clearedByTypingRef.current = false;
      onSelect(selectedKey);
      if (clearOnSelect) {
        setCurrentKey("");
        setCurrentValue("");
        applyFilter("");
        return;
      }
      setCurrentKey(selectedKey);
      setCurrentValue(selectedValue);
      /*
        Every option stays listed rather than only those matching what was just selected. Narrowing the list to
        the selection left the next opening of the dropdown showing that one entry, so a field that already had
        a value could not be browsed — only retyped.
      */
      applyFilter("");
    }
  };

  /*
    Keep selection in sync with external initialKey and available data (e.g., when filters load from URL).

    Keyed on the caller's selection rather than on `data`, which changes identity on every search round: syncing
    off that overwrote whatever the user was typing with the current selection, so a field that had a value
    could never be searched again.
  */
  // Starts unset so the caller's opening selection is adopted on the first render that can resolve its name.
  const syncedKeyRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (initialKey) {
      if (syncedKeyRef.current === initialKey) return;

      const newValue = data?.find((el) => el.key === initialKey)?.value?.toString() || "";
      // The options that name this key have not arrived yet — wait for them rather than blanking the field.
      if (!newValue) return;

      syncedKeyRef.current = initialKey;
      clearedByTypingRef.current = false;
      setCurrentKey(initialKey);
      setCurrentValue(newValue);
      // As above: adopting a selection must not prune what the dropdown will show next time it opens.
      applyFilter("");
    } else if (syncedKeyRef.current) {
      /*
        Clear only a selection this effect put there. A selection the user made through the dropdown is not the
        caller's to withdraw, and clearing it here wiped the field on the next search.
      */
      syncedKeyRef.current = initialKey;
      /*
        Typing is what withdrew the key, so the field already holds what the user wants searched. Dropping the
        ref above first matters: this effect also reruns on every search round, and a key left there would take
        this branch on the next one.
      */
      if (clearedByTypingRef.current) return;

      setCurrentKey("");
      setCurrentValue("");
      applyFilter("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey, data]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const selectedValue = data?.find((el) => el.key === currentKey)?.value;
      if (currentValue !== undefined && currentValue !== null && currentValue !== selectedValue) {
        if (onTypeSearch) {
          onTypeSearch(currentValue);
        }
      }
    }, TYPE_SEARCH_WAITING_TIME);

    return () => clearTimeout(debounce);
  }, [currentValue, currentKey]);

  return { currentValue, currentKey, options, handleSearch, handleSelect };
};
